import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import UserManagementService from "../../services/UserManagementService";
import { IAccountType } from "../../interfaces/userManagement.interface";
import "./TestUserManagement.scss";

const TestUserManagement: React.FC = () => {
  const { userProfile } = useAuth();
  const [accountTypes, setAccountTypes] = useState<IAccountType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAccountTypes = () => {
      try {
        const types = UserManagementService.getAvailableAccountTypes(
          userProfile || undefined
        );
        setAccountTypes(types);
        console.log("Available account types:", types);
      } catch (error) {
        console.error("Error loading account types:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAccountTypes();
  }, [userProfile]);

  const testSelfRegistration = async () => {
    try {
      const testData = {
        name: "Test User",
        username: "testuser",
        email: "test@example.com",
        password: "TestPass123",
        confirmPassword: "TestPass123",
        phoneNumber: "1234567890",
        identifier: "TEST_ORG",
        organizationName: "Test Organization",
        userType: "TRANSPORTER" as const,
      };

      console.log("Testing self-registration with data:", testData);
      const result = await UserManagementService.selfRegister(testData);
      console.log("Self-registration result:", result);
    } catch (error) {
      console.error("Self-registration test failed:", error);
    }
  };

  const testUserCreation = async () => {
    if (!userProfile) {
      console.log("No user profile, skipping user creation test");
      return;
    }

    try {
      const testData = {
        name: "Test Admin User",
        username: "testadmin",
        email: "admin@example.com",
        password: "AdminPass123",
        confirmPassword: "AdminPass123",
        phoneNumber: "9876543210",
        identifier: "ADMIN_ORG",
        organizationName: "Admin Organization",
        userType: "TRANSPORTER" as const,
      };

      console.log("Testing user creation with data:", testData);
      const result = await UserManagementService.createUser(
        testData,
        userProfile
      );
      console.log("User creation result:", result);
    } catch (error) {
      console.error("User creation test failed:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="test-user-management">
        <div className="loading">Loading account types...</div>
      </div>
    );
  }

  return (
    <div className="test-user-management">
      <h2>User Management Test Page</h2>

      <div className="user-info">
        <h3>Current User Info</h3>
        <p>
          <strong>Name:</strong> {userProfile?.userFullName || "Not logged in"}
        </p>
        <p>
          <strong>Role:</strong> {userProfile?.role?.name || "No role"}
        </p>
        <p>
          <strong>Organization:</strong>{" "}
          {userProfile?.org?.name || "No organization"}
        </p>
      </div>

      <div className="account-types">
        <h3>Available Account Types</h3>
        {accountTypes.length === 0 ? (
          <p>No account types available for current user</p>
        ) : (
          <ul>
            {accountTypes.map((type) => (
              <li key={type.type}>
                <strong>{type.title}</strong> - {type.description}
                <br />
                <small>Permissions: {type.permissions.join(", ")}</small>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="test-actions">
        <h3>Test Actions</h3>
        <div className="action-buttons">
          <button onClick={testSelfRegistration} className="test-button">
            Test Self Registration
          </button>
          <button onClick={testUserCreation} className="test-button">
            Test User Creation (Admin)
          </button>
        </div>
      </div>

      <div className="navigation">
        <h3>Navigation</h3>
        <div className="nav-links">
          <a href="/user-onboarding" className="nav-link">
            Go to User Onboarding
          </a>
          <a href="/sign-up" className="nav-link">
            Go to Original Sign Up
          </a>
        </div>
      </div>
    </div>
  );
};

export default TestUserManagement;
